import { isProductLiveOrOwnFilter, query } from "@neru/base/constants";
import {
  activeVersionFragment,
  useQuery,
  useValidSession,
} from "@neru/base/hooks";
import {
  type APIParams,
  type FilterParams,
  type GetEntitiesListResponse,
  type GetProductQuery,
  type GetProductsQuery,
  ProductType_Enum,
} from "@neru/base/types";
import { getAccountId } from "@neru/base/utils";
import { deepMerge } from "@neru/base/utils/deep-merge";
import { gql } from "graphql-request";

export type ProductData = GetProductQuery["Products"][number];

export interface GetProductsResponse extends GetEntitiesListResponse {
  data: {
    Products: Array<
      Omit<GetProductsQuery["Products"][number], "ProductVersion"> & {
        icon?: string | null;
        isLive: boolean;
        isPublic: boolean;
        short_description?: string;
        tags: Array<string>;
      }
    >;
  };
}

const liveOrLatestVersion = ({
  LatestVersion = [],
  ProductVersion,
  ...rest
}: GetProductsQuery["Products"][number]) => ({
  ...rest,
  LatestVersion,
  icon: (ProductVersion ?? LatestVersion[0])?.icon,
  isLive: Boolean(ProductVersion),
  isPublic: rest.type === ProductType_Enum.Public,
  short_description: (ProductVersion ?? LatestVersion[0])?.short_description,
  tags: ((ProductVersion ?? LatestVersion[0])?.VersionTags ?? []).map(
    ({ Tag }) => Tag?.name,
  ),
});

export const useGetProductsQuery = (
  params: APIParams,
  showPrivate: boolean = false,
): GetProductsResponse => {
  const where = {
    _and: {
      ...query.IS_NOT_DELETED,
      ...isProductLiveOrOwnFilter(
        useValidSession() && showPrivate ? getAccountId() : null,
      ),
    },
  };

  const GET_PRODUCTS_QUERY = gql`
    ${activeVersionFragment}
    query getProducts(
      $where: Products_bool_exp
      $orderBy: [Products_order_by!]
      $offset: Int
      $limit: Int
      $withDesc: Boolean!
      $withDocs: Boolean!
    ) {
      Products(
        order_by: $orderBy
        offset: $offset
        limit: $limit
        where: $where
      ) {
        id
        name
        owner
        owner_name
        priority
        type
        ProductVersion {
          ...activeVersionFields
        }
        LatestVersion: ProductVersions(
          order_by: [{ updated_at: desc }]
          limit: 1
        ) {
          ...activeVersionFields
        }
      }
      Products_aggregate(where: $where) {
        aggregate {
          count(columns: id)
        }
      }
    }
  `;

  const { data, error, isValidating } = useQuery<GetProductsQuery>(
    GET_PRODUCTS_QUERY,
    {
      ...params,
      includeFields: { withDesc: false, withDocs: false },
      where: deepMerge(where, params.where ?? {}) as FilterParams,
    },
  );

  return {
    data: {
      Products: data?.Products.map(liveOrLatestVersion) ?? [],
    },
    error: error?.message,
    loading: isValidating,
    total: data?.Products_aggregate?.aggregate?.count ?? 0,
  };
};
